import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

function StudentReport() {
  const storedSession = localStorage.getItem("currentSessionId") || "";
  const [session] = useState(storedSession);

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);

  const [searchType, setSearchType] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");

  const printRef = useRef();

  /* ================= FETCH CLASSES ================= */
  useEffect(() => {
    if (!session) return;

    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${session}`
      )
      .then((res) => setClasses(res.data))
      .catch((err) => console.error(err));
  }, [session]);

  /* ================= FETCH ALL STUDENTS ================= */
  useEffect(() => {
    if (!session) return;

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, {
        params: {
          sessionId: session,
        },
      })
      .then((res) => {
        setStudents(res.data);
        setFilteredStudents(res.data);
        setAllStudents(res.data);
      })
      .catch((err) => console.error(err));
  }, [session]);

  /* ================= FETCH SECTIONS ================= */
  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection("");
      return;
    }

    const className = classes.find(
      (c) => c._id === selectedClass
    )?.className;

    if (!className) return;

    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(
          className
        )}/getSectionsByClassName?sessionId=${session}`
      )
      .then((res) => {
        const data = Array.isArray(res.data)
          ? res.data
          : res.data.sections || [];

        setSections(data);
      })
      .catch((err) => console.error(err));
  }, [selectedClass, session, classes]);

  /* ================= CASE INSENSITIVE FILTER ================= */
  useEffect(() => {
    let filtered = [...students];

    const value = searchValue.toLowerCase();

    if (searchType === "name" && value) {
      filtered = filtered.filter(
        (s) =>
          s.firstName?.toLowerCase().includes(value) ||
          s.lastName?.toLowerCase().includes(value) ||
          s.rollNo?.toString().includes(value)
      );
    }

    if (searchType === "admission" && value) {
      filtered = filtered.filter((s) =>
        s.admission_id?.toString().includes(value)
      );
    }

    if (searchType === "financial" && value) {
      filtered = filtered.filter(
        (s) => s.financial?.toLowerCase() === value
      );
    }

    if (searchType === "category" && value) {
      filtered = filtered.filter(
        (s) => s.category?.toLowerCase() === value
      );
    }

    if (searchType === "status" && value) {
      filtered = filtered.filter(
        (s) => s.status?.toLowerCase() === value
      );
    }

    if (searchType === "gender" && value) {
      filtered = filtered.filter(
        (s) => s.gender?.toLowerCase() === value
      );
    }

    if (searchType === "class" && selectedClass && selectedSection) {
      const className = classes.find(
        (c) => c._id === selectedClass
      )?.className;

      filtered = filtered.filter(
        (s) =>
          s.class === className &&
          s.section === selectedSection
      );
    }

    setFilteredStudents(filtered);
  }, [
    searchValue,
    searchType,
    selectedClass,
    selectedSection,
    students,
    classes,
  ]);

  /* ================= EXPORT ================= */
  const exportToExcel = () => {
    const data = filteredStudents.map((stu, index) => ({
      "Sr No.": index + 1,
      "Admission No": stu.admission_id || "",
      Name: `${stu.firstName || ""} ${stu.lastName || ""}`.trim(),
      Class: stu.class || "",
      Section: stu.section || "",
      Gender: stu.gender || "",

      // NEW FIELDS
      "Contact No.": stu.phoneNo || "",
      Address: stu.address || "",
      "Father Name": stu.fatherName || "",
      "Mother Name": stu.motherName || "",

      Category: stu.category || "",
      Financial: stu.financial || "",
      Status: stu.status || "",
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Students"
    );

    XLSX.writeFile(
      workbook,
      "Student_Report.xlsx"
    );
  };

  /* ================= PRINT ================= */
  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;

    const newWindow = window.open(
      "",
      "",
      "width=900,height=650"
    );

    newWindow.document.write(`
      <html>
        <head>
          <title>Student Report</title>

          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            th,
            td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: center;
            }

            th {
              background: #f3f4f6;
            }

            h2 {
              text-align: center;
              margin-bottom: 20px;
            }
          </style>
        </head>

        <body>
          <h2>Student Report</h2>

          ${printContents}
        </body>
      </html>
    `);

    newWindow.document.close();
    newWindow.print();
  };

  /* ================= BADGES ================= */
  const totalStudents = allStudents.length;

  const active = allStudents.filter(
    (s) => s.status?.toLowerCase() === "active"
  ).length;

  const inactive = totalStudents - active;

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto bg-white shadow-xl rounded-xl p-8">

        {/* HEADER */}
        <h2 className="text-2xl font-bold mb-6 text-gray-700">
          Student Details Report
        </h2>

        {/* SUMMARY */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">

          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
            <p className="text-sm text-blue-600">
              Total Students
            </p>

            <p className="text-2xl font-bold text-blue-700">
              {totalStudents}
            </p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
            <p className="text-sm text-green-600">
              Active
            </p>

            <p className="text-2xl font-bold text-green-700">
              {active}
            </p>
          </div>

          <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
            <p className="text-sm text-red-600">
              Inactive
            </p>

            <p className="text-2xl font-bold text-red-700">
              {inactive}
            </p>
          </div>

        </div>

        {/* FILTERS */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">

          <select
            value={searchType}
            onChange={(e) => {
              setSearchType(e.target.value);
              setSearchValue("");
            }}
            className="border rounded px-3 py-2 shadow-sm"
          >
            <option value="">
              Select Search Type
            </option>

            <option value="name">
              Name / Roll
            </option>

            <option value="admission">
              Admission No
            </option>

            <option value="financial">
              Financial
            </option>

            <option value="category">
              Category
            </option>

            <option value="status">
              Status
            </option>

            <option value="gender">
              Gender
            </option>

            <option value="class">
              Class & Section
            </option>
          </select>

          {["name", "admission"].includes(searchType) && (
            <input
              type="text"
              placeholder="Enter value"
              value={searchValue}
              onChange={(e) =>
                setSearchValue(e.target.value)
              }
              className="border rounded px-3 py-2 shadow-sm"
            />
          )}

          {[
            "financial",
            "category",
            "status",
            "gender",
          ].includes(searchType) && (
            <select
              value={searchValue}
              onChange={(e) =>
                setSearchValue(e.target.value)
              }
              className="border rounded px-3 py-2 shadow-sm"
            >
              <option value="">
                Select
              </option>

              {searchType === "financial" && (
                <>
                  <option value="apl">
                    APL
                  </option>

                  <option value="bpl">
                    BPL
                  </option>
                </>
              )}

              {searchType === "category" && (
                <>
                  <option value="general">
                    General
                  </option>

                  <option value="obc">
                    OBC
                  </option>

                  <option value="sc">
                    SC
                  </option>
                </>
              )}

              {searchType === "status" && (
                <>
                  <option value="active">
                    Active
                  </option>

                  <option value="inactive">
                    Inactive
                  </option>
                </>
              )}

              {searchType === "gender" && (
                <>
                  <option value="male">
                    Male
                  </option>

                  <option value="female">
                    Female
                  </option>
                </>
              )}
            </select>
          )}

          {searchType === "class" && (
            <>
              <select
                value={selectedClass}
                onChange={(e) =>
                  setSelectedClass(e.target.value)
                }
                className="border rounded px-3 py-2 shadow-sm"
              >
                <option value="">
                  Select Class
                </option>

                {classes.map((cls) => (
                  <option
                    key={cls._id}
                    value={cls._id}
                  >
                    {cls.className}
                  </option>
                ))}
              </select>

              <select
                value={selectedSection}
                onChange={(e) =>
                  setSelectedSection(e.target.value)
                }
                className="border rounded px-3 py-2 shadow-sm"
                disabled={!selectedClass}
              >
                <option value="">
                  Select Section
                </option>

                {sections.map((sec) => (
                  <option
                    key={sec._id}
                    value={sec.sectionName}
                  >
                    {sec.sectionName}
                  </option>
                ))}
              </select>
            </>
          )}

        </div>

        {/* ACTION BUTTONS */}
        <div className="flex justify-end gap-4 mb-4">

          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded shadow hover:bg-green-700"
          >
            Export Excel
          </button>

          <button
            onClick={handlePrint}
            className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700"
          >
            Print
          </button>

        </div>

        {/* TABLE */}
        <div
          ref={printRef}
          className="overflow-x-auto"
        >
          <table className="min-w-full border text-center">

            <thead className="bg-gray-200">
              <tr>

                <th className="border px-2 py-2">
                  Sr
                </th>

                <th className="border px-2 py-2">
                  Admission
                </th>

                <th className="border px-2 py-2">
                  Name
                </th>

                <th className="border px-2 py-2">
                  Class
                </th>

                <th className="border px-2 py-2">
                  Section
                </th>

                <th className="border px-2 py-2">
                  Gender
                </th>

                {/* NEW */}
                <th className="border px-2 py-2">
                  Contact No.
                </th>

                <th className="border px-2 py-2">
                  Address
                </th>

                <th className="border px-2 py-2">
                  Father Name
                </th>

                <th className="border px-2 py-2">
                  Mother Name
                </th>

                <th className="border px-2 py-2">
                  Category
                </th>

                <th className="border px-2 py-2">
                  Financial
                </th>

                <th className="border px-2 py-2">
                  Status
                </th>

              </tr>
            </thead>

            <tbody>
              {filteredStudents.map((s, i) => (
                <tr
                  key={s._id}
                  className="hover:bg-gray-50"
                >

                  <td className="border px-2 py-2">
                    {i + 1}
                  </td>

                  <td className="border px-2 py-2">
                    {s.admission_id || "-"}
                  </td>

                  <td className="border px-2 py-2">
                    {s.firstName || ""}{" "}
                    {s.lastName || ""}
                  </td>

                  <td className="border px-2 py-2">
                    {s.class || "-"}
                  </td>

                  <td className="border px-2 py-2">
                    {s.section || "-"}
                  </td>

                  <td className="border px-2 py-2">
                    {s.gender || "-"}
                  </td>

                  {/* NEW */}
                  <td className="border px-2 py-2">
                    {s.phoneNo || "-"}
                  </td>

                  <td className="border px-2 py-2">
                    {s.address || "-"}
                  </td>

                  <td className="border px-2 py-2">
                    {s.fatherName || "-"}
                  </td>

                  <td className="border px-2 py-2">
                    {s.motherName || "-"}
                  </td>

                  <td className="border px-2 py-2">
                    {s.category || "-"}
                  </td>

                  <td className="border px-2 py-2">
                    {s.financial || "-"}
                  </td>

                  <td className="border px-2 py-2">
                    <span
                      className={`px-2 py-1 text-white rounded ${
                        s.status?.toLowerCase() ===
                        "active"
                          ? "bg-green-600"
                          : "bg-red-600"
                      }`}
                    >
                      {s.status || "-"}
                    </span>
                  </td>

                </tr>
              ))}
            </tbody>

          </table>
        </div>

      </div>
    </div>
  );
}

export default StudentReport;